import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  GraduationCap, 
  Loader2, 
  AlertTriangle, 
  BookOpen, 
  Tag, 
  CheckSquare,
  Info
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { format } from 'date-fns';

// Add Course type for local use
interface CourseWithBanner {
  id: string;
  title: string;
  description?: string | null;
  event_type: string;
  status: string;
  hours: number;
  max_participants?: number | null;
  venue_details?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  check_in_code?: string | null;
  category?: string | null;
  subject_area?: string | null;
  created_at: string;
  updated_at: string;
  target_audience: string;
  state_requirements?: any;
  presenter_bio?: string | null;
  presenter_image_url?: string | null;
  event_banner_url?: string | null;
  presenter?: string | null;
  location?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile, user } = useAuth();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);

  if (id === "new") {
    useEffect(() => {
      navigate('/admin/courses/create');
    }, [navigate]);
    
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { 
    data: course, 
    isLoading, 
    error: courseError,
    isError: isCourseError
  } = useQuery<CourseWithBanner | null>({
    queryKey: ['course-details', id],
    queryFn: async () => {
      try {
        if (id === "new") {
          throw new Error("Redirecting to course creation page");
        }
        
        const { data, error } = await supabase
          .from('courses')
          .select('*')
          .eq('id', id)
          .single() as { data: any, error: any };

        if (error) {
          throw error;
        }

        // Ensure event_banner_url is present for type safety
        const {
          event_banner_url,
          ...rest
        } = data;
        return {
          ...rest,
          event_banner_url: event_banner_url || null,
        };
      } catch (error: any) {
        console.error('Error loading course:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load course details",
          variant: "destructive",
        });
        throw error;
      }
    },
    enabled: id !== "new",
  });

  const { 
    data: registration,
    error: registrationError,
    isError: isRegistrationError
  } = useQuery({
    queryKey: ['course-registration', id, profile?.id],
    enabled: !!profile?.id && !!id,
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('course_registrations')
          .select('*')
          .eq('course_id', id)
          .eq('user_id', profile?.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching registration:', error);
        }

        return data;
      } catch (error: any) {
        console.error('Error fetching registration:', error);
        return null;
      }
    },
  });

  const handleEnroll = async () => {
    setEnrollmentError(null);
    
    if (!user?.id) {
      setEnrollmentError("Please log in to enroll in courses");
      toast({
        title: "Authentication Required",
        description: "Please log in to enroll in courses",
        variant: "destructive",
      });
      return;
    }

    setIsEnrolling(true);
    try {
      const { data: existingReg, error: checkError } = await supabase
        .from('course_registrations')
        .select('id')
        .eq('course_id', id)
        .eq('user_id', profile?.id)
        .maybeSingle();
        
      if (checkError) {
        throw new Error("Failed to check enrollment status");
      }
      
      if (existingReg) {
        throw new Error("You are already enrolled in this course");
      }

      const { error } = await supabase
        .from('course_registrations')
        .insert([
          {
            course_id: id,
            user_id: profile?.id,
            status: 'registered'
          }
        ]);

      if (error) {
        if (error.code === '23505') {
          throw new Error("You are already enrolled in this course");
        }
        throw error;
      }

      toast({
        title: "Success",
        description: "Successfully enrolled in the course!",
      });
      
      window.location.reload();
    } catch (error: any) {
      console.error('Error enrolling in course:', error);
      setEnrollmentError(error.message || "Failed to enroll in the course");
      toast({
        title: "Error",
        description: error.message || "Failed to enroll in the course",
        variant: "destructive",
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!registration?.id) return;

    setIsEnrolling(true);
    setEnrollmentError(null);
    try {
      const { error } = await supabase
        .from('course_registrations')
        .delete()
        .eq('id', registration.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Successfully cancelled your registration",
      });
      
      window.location.reload();
    } catch (error: any) {
      console.error('Error cancelling registration:', error);
      setEnrollmentError(error.message || "Failed to cancel registration");
      toast({
        title: "Error",
        description: error.message || "Failed to cancel registration",
        variant: "destructive",
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const formatCategoryName = (key: string) => {
    const categoryMapping = {
      'behavioralIntervention': 'Positive Behavioral Intervention Strategies',
      'readingPreparation': 'Reading Preparation',
      'keyWarningSigns': 'Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents',
      'suicidePrevention': 'Suicide Prevention',
      'englishLearners': 'English Learners',
      'americanIndianHistory': 'American Indian History and Culture',
      'racial': 'Racial, Cultural, and Socioeconomic Groups',
      'religion': 'Religion',
      'systemicRacism': 'Systemic Racism',
      'genderIdentity': 'Gender Identity, Including Transgender Students',
      'sexualOrientation': 'Sexual Orientation',
      'languageDiversity': 'Language Diversity',
      'disabilities': 'Individuals with Disabilities and Mental Health Concerns'
    };

    return categoryMapping[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };
  
  const getEnabledCategoriesWithHours = () => {
    if (!course?.state_requirements) return [];
    
    const requirements = course.state_requirements as any;
    const result = [];
    
    const categories = [
      'behavioralIntervention',
      'readingPreparation',
      'keyWarningSigns',
      'suicidePrevention',
      'englishLearners',
      'americanIndianHistory'
    ];
    
    for (const category of categories) {
      if (requirements[category]) {
        const hours = requirements[`${category}Hours`] || 0;
        result.push({
          name: formatCategoryName(category),
          hours: parseFloat(hours)
        });
      }
    }
    
    if (requirements.culturalCompetency?.enabled) {
      const subcategories = [
        'racial',
        'religion',
        'systemicRacism',
        'genderIdentity',
        'sexualOrientation',
        'languageDiversity',
        'disabilities'
      ];
      
      for (const subcategory of subcategories) {
        if (requirements.culturalCompetency[subcategory]) {
          const hours = requirements.culturalCompetency[`${subcategory}Hours`] || 0;
          result.push({
            name: `Cultural Competency: ${formatCategoryName(subcategory)}`,
            hours: parseFloat(hours)
          });
        }
      }
    }
    
    return result;
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    // Always extract the date part and format as local date
    const dateOnly = dateString.split('T')[0];
    const date = new Date(dateOnly + 'T12:00:00'); // Use noon to avoid timezone issues
    return format(date, 'MMMM d, yyyy');
  };

  if (isLoading) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-lg font-section text-muted-foreground">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (isCourseError || !course) {
    return (
      <div className="container mx-auto pt-6">
        <div className="max-w-2xl mx-auto text-center">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h2 className="text-3xl font-title font-bold text-primary mb-2">Course not found</h2>
          <p className="text-muted-foreground mb-6 font-body text-lg">
            {courseError instanceof Error 
              ? courseError.message 
              : "The course you're looking for could not be loaded."}
          </p>
          <Button 
            onClick={() => navigate('/courses')}
            className="mt-4 bg-primary hover:bg-primary/90 font-section text-lg px-8 py-6 h-auto"
            size="lg"
          >
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  const stateRequirements = course?.state_requirements as { 
    culturalCompetency?: { 
      enabled: boolean;
      racial?: boolean;
      religion?: boolean;
      systemicRacism?: boolean;
      genderIdentity?: boolean;
      sexualOrientation?: boolean;
      languageDiversity?: boolean;
      disabilities?: boolean;
    };
    [key: string]: any;
  } | null;
  
  const categoriesWithHours = getEnabledCategoriesWithHours();

  return (
    <div className="w-full px-0 py-4 animate-fade-in">
      {/* Hero Section */}
      <div className="relative w-full">
        {/* Banner background */}
        {course.event_banner_url ? (
          <img src={course.event_banner_url} alt="Event Banner" className="w-full h-56 md:h-72 object-cover" />
        ) : (
          <div className="w-full h-56 md:h-72 bg-gradient-to-r from-accent to-accent-subtle flex items-center justify-center">
            <BookOpen className="h-16 w-16 text-white/40" />
          </div>
        )}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        {/* Floating glass card */}
        <div className="absolute left-1/2 top-3/4 md:top-2/3 transform -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-3xl z-10">
          <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-accent-light/30">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-extrabold font-title text-primary mb-1 drop-shadow truncate">{course.title}</h1>
              <div className="flex flex-wrap gap-4 text-primary/80 text-xs items-center">
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{course.hours} {course.hours === 1 ? 'Hour' : 'Hours'}</span>
                {course.event_type === 'live' && course.start_date && (
                  <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{formatDate(course.start_date)}</span>
                )}
                {course.presenter && (
                  <span className="flex items-center gap-1"><GraduationCap className="h-4 w-4" />{course.presenter}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Presenter avatar */}
              {(course.presenter_image_url || course.presenter) && (
                <img src={course.presenter_image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(course.presenter || 'Presenter')}&background=0f8f8c&color=fff&size=128`} alt="Presenter" className="h-14 w-14 object-cover rounded-full border-4 border-white shadow-md -mt-8 md:mt-0" />
              )}
              {/* Enroll/Cancel button */}
              {registration ? (
                <Button 
                  variant="accent"
                  onClick={handleCancelRegistration}
                  disabled={isEnrolling}
                  size="lg"
                  className="font-section shadow-lg hover:shadow-xl transition-all bg-[#0f8f8c] hover:bg-[#0f8f8c]/90"
                >
                  {isEnrolling ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cancelling...</>) : 'Cancel Registration'}
                </Button>
              ) : (
                <Button 
                  onClick={handleEnroll}
                  disabled={isEnrolling}
                  size="lg"
                  className="shadow-lg hover:shadow-xl transition-all duration-300 bg-accent hover:bg-accent/90 font-section"
                >
                  {isEnrolling ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enrolling...</>) : 'Enroll Now'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {enrollmentError && (
        <div className="max-w-3xl mx-auto mt-4">
          <Alert variant="destructive" className="border-destructive/60 bg-destructive/10">
            <AlertTitle className="font-heading">Enrollment Error</AlertTitle>
            <AlertDescription className="font-body">{enrollmentError}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main Content: Responsive two-column layout */}
      <div className="w-full mt-24 md:mt-32 px-1 flex flex-col md:flex-row gap-4">
        {/* Left column: Main course info */}
        <div className="flex-1 space-y-3 min-w-0">
          {/* About the Course */}
          <section className="bg-white rounded-2xl shadow-md p-3 border border-accent-light/20 w-full text-center">
            <div className="flex flex-col items-center gap-2 mb-2">
              <BookOpen className="h-5 w-5 text-accent" />
              <h2 className="font-heading text-lg text-primary">About this Course</h2>
            </div>
            <p className="text-muted-foreground whitespace-pre-line font-body leading-relaxed text-sm">
              {course.description || "No description available"}
            </p>
          </section>
          {/* Requirements Pills */}
          {categoriesWithHours.length > 0 && (
            <section className="bg-white rounded-2xl shadow-md p-3 border border-accent-light/20 w-full text-center">
              <div className="flex flex-col items-center gap-2 mb-2">
                <Info className="h-5 w-5 text-accent" />
                <h3 className="font-heading text-base text-primary">Requirements</h3>
              </div>
              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {categoriesWithHours.map((category, index) => (
                  <span key={index} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-subtle text-primary text-xs font-medium border border-accent-light/30 shadow-sm">
                    {category.name}
                    <Badge variant="outline" className="ml-1 border-accent font-body">
                      {category.hours} {category.hours === 1 ? 'Hour' : 'Hours'}
                    </Badge>
                  </span>
                ))}
              </div>
            </section>
          )}
          {/* Other Details Card */}
          <section className="bg-white rounded-2xl shadow-md p-3 border border-accent-light/20 w-full text-center">
            <div className="flex flex-col items-center gap-2 mb-2">
              <Tag className="h-5 w-5 text-accent" />
              <h3 className="font-heading text-base text-primary">Course Details</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs justify-items-center">
              <div className="flex items-center gap-2 justify-center"><BookOpen className="h-4 w-4 text-primary" /><span>{course.subject_area}</span></div>
              <div className="flex items-center gap-2 justify-center"><Users className="h-4 w-4 text-primary" /><span>{course.max_participants || 'Unlimited'} participants</span></div>
              <div className="flex items-center gap-2 justify-center"><CheckSquare className="h-4 w-4 text-primary" /><span>{course.category}</span></div>
            </div>
          </section>
        </div>
        {/* Right column: Presenter card and Schedule */}
        <aside className="w-full md:w-80 flex-shrink-0 space-y-3">
          {/* Presenter Card */}
          <section className="bg-white rounded-2xl shadow-md p-3 border border-accent-light/20 sticky top-28 w-full text-center">
            <div className="flex flex-col items-center text-center gap-3">
              <img src={course.presenter_image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(course.presenter || 'Presenter')}&background=0f8f8c&color=fff&size=128`} alt="Presenter" className="h-24 w-24 object-cover rounded-full border-4 border-accent shadow mb-2" />
              <h3 className="font-semibold text-primary text-lg">{course.presenter}</h3>
              <p className="text-muted-foreground text-sm whitespace-pre-line font-body">
                {course.presenter_bio || "No presenter bio provided."}
              </p>
            </div>
          </section>
          {/* Schedule Card */}
          {(course.venue_details || (course.event_type === 'live' && (course.start_date || course.start_time))) && (
            <section className="bg-white rounded-2xl shadow-md p-3 border border-accent-light/20 w-full text-center">
              <div className="flex flex-col items-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-accent" />
                <h3 className="font-heading text-base text-primary">Schedule & Venue</h3>
              </div>
              <div className="space-y-2 text-xs">
                {(course.venue_details || course.location) && (
                  <div><span className="font-semibold">Venue:</span> {course.venue_details || course.location}</div>
                )}
                {course.event_type === 'live' && course.start_date && (<div><span className="font-semibold">Date:</span> {formatDate(course.start_date)}</div>)}
                {course.event_type === 'live' && course.start_time && course.end_time && (<div><span className="font-semibold">Time:</span> {course.start_time} - {course.end_time}</div>)}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CourseDetails;
